import React, { useEffect, useState } from "react";
import { injectIntl, intlShape } from "@ai360/core/intl";
import { defineMessages } from "@ai360/core/intl";
import { AppHelpers, UserAPI } from "@ai360/core";
import { getUser } from "~/login";

import { DialogBox, DialogBoxFooterType, Menu, NoLink } from "~/core";
import { getFirstName, getUserSystemProperties } from "~/login";
import { Navigation } from "~/navigation";
import SelectCompany from "../../login/components/select-company";
import { ThemeClassNames, getCurrentTheme } from "~/theme";
import { getIsEnabled } from "~/action-panel/selectors";

import { LocationIcon } from "~/core/icons";

import "./header.css";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { logoutUser, reloginUser } from "~/login/actions";

const messages = defineMessages({
    greeting: { id: "header.greeting", defaultMessage: "Hello" },
    about: { id: "header.about", defaultMessage: "About" },
    account: { id: "header.account", defaultMessage: "Account" },
    orgLevelQuickChanger: {
        id: "header.orgLevelQuickChanger",
        defaultMessage: "Org Level Quick Changer",
    },
    logOut: { id: "header.logOut", defaultMessage: "Log Out" },
    preferences: { id: "header.preferences", defaultMessage: "Preferences" },
});

interface IProps {
    intl: intlShape;
    themeClassName: ThemeClassNames;
    firstName: string;
    onLogoutUser: () => void;
    loadDetailPage: (childNameCode: string, parentNameCode: string) => void;
    closeSidePanel: () => void;
    isEnabled: boolean;
    systemProperties: any;
    userRole: UserAPI.IUserRole;
    user: UserAPI.IUser;
    onReloginUser: (history: any) => void;
}

interface IMenuItem {
    key: number;
    label: string;
    disabled?: boolean;
    action: () => void;
}

const Header_ = (props: IProps) => {
    const dispatch = useAppDispatch();
    const themeClassName = useAppSelector(getCurrentTheme);
    const firstName = useAppSelector(getFirstName);
    const isEnabled = useAppSelector(getIsEnabled);
    const systemProperties = useAppSelector(getUserSystemProperties);
    const { role: userRole } = useAppSelector((state) => getUser(state));
    const user = useAppSelector(getUser);
    const { formatMessage } = props.intl;

    // RTK Query's subscription bookkeeping dispatches synchronously during render on
    // every render it runs, not just the first. In this app that dispatch cascades
    // through the redux-saga/redux-batched-actions middleware chain into a second,
    // downstream dispatch whose store notification reaches Header_'s own connected
    // selectors (getIsEnabled, getUserSystemProperties, ...) synchronously — i.e.
    // Header_ scheduling an update on itself while still mid-render. Skipping until
    // after mount closes the initial-mount race; staying skipped once the menu data
    // has already loaded once closes the recurrence on every later re-render too,
    // since this data never needs to be re-fetched on Header_'s account.
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => {
        setHasMounted(true);
    }, []);

    const [hasFetchedMenus, setHasFetchedMenus] = useState(false);
    const { data, error, isLoading } = UserAPI.useGetSystemCustomMenusQuery(undefined, {
        skip: !hasMounted || hasFetchedMenus,
    });
    useEffect(() => {
        if (data || error) {
            setHasFetchedMenus(true);
        }
    }, [data, error]);

    const [isSelectCompanyOpen, setIsSelectCompanyOpen] = useState(false);
    const [menuItems, setMenuItems] = useState<IMenuItem[]>([]);

    useEffect(() => {
        if (userRole != null) {
            const items = [];
            items.push({
                key: 0,
                label: formatMessage(messages.preferences),
                disabled: !isEnabled,
                action: () => props.loadDetailPage("1", "101"),
            });
            if (userRole?.account) {
                items.push({
                    key: 1,
                    label: formatMessage(messages.account),
                    action: () => props.loadDetailPage("3", "101"),
                });
            }
            if (data && !isLoading && !error) {
                items.push(
                    ...data.map((x) => ({
                        key: x.sortOrder + menuItems.length,
                        label: x.name,
                        action: () =>
                            window.open(
                                ((u) =>
                                    /^https?:\/\//i.test(u)
                                        ? u
                                        : `https://${u.replace(/^[\\/]+/, "")}`)(x.url.trim()),
                                "_blank",
                                "noopener,noreferrer"
                            ),
                    }))
                );
            }

            items.push({
                key: 2,
                label: formatMessage(messages.about),
                action: () => props.loadDetailPage("4", "100"),
            });
            items.push({
                key: 3,
                label: formatMessage(messages.logOut),
                action: () => dispatch(logoutUser()),
            });
            setMenuItems([...items]);
        } else if (error) {
            console.error(error);
        }
    }, [data, error, isLoading, userRole]);

    const toggleOpenSelectCompany = (isOpen) => {
        setIsSelectCompanyOpen(isOpen);
    };

    const onRelogin = (history) => {
        toggleOpenSelectCompany(false);
        dispatch(reloginUser(history));
    };

    const formatImage = (image) => {
        return !AppHelpers.fileIsSvgMarkup(image)
            ? AppHelpers.base64StringToBase64Image(image)
            : image;
    };
    const displayLogo =
        themeClassName.indexOf("-light") !== -1
            ? formatImage(systemProperties.systemLogoLight)
            : formatImage(systemProperties.systemLogoDark);
    const selectedCustomerName =
        !userRole.orgLevelQuickChanger || user.orgLevelList.length === 0
            ? null
            : user.lastUsedCustomerName;
    const selectedLocationName =
        !userRole.orgLevelQuickChanger || user.orgLevelList.length === 0
            ? ""
            : selectedCustomerName != null && selectedCustomerName != ""
            ? selectedCustomerName
            : user.lastUsedLocationGuid == null ||
              user.lastUsedLocationGuid === "" ||
              user.orgLevelList.find((org) => org.orgLevelGuid === user.lastUsedLocationGuid) ==
                  null
            ? user.companyName
            : user.orgLevelList.find((org) => org.orgLevelGuid === user.lastUsedLocationGuid)
                  .orgLevelName;

    const navigationProps = { loadDetailPage: props.loadDetailPage };

    return (
        <div className="header">
            <div className="logo-row">
                <span onClick={props.closeSidePanel}>
                    <div className={"logo"}>
                        {AppHelpers.fileIsSvgMarkup(displayLogo) ? (
                            <div
                                className={"system-logo svg-image"}
                                dangerouslySetInnerHTML={{
                                    __html: displayLogo,
                                }}
                            ></div>
                        ) : (
                            <div className={"system-logo"}>
                                <img src={displayLogo} alt={systemProperties.systemName} />
                            </div>
                        )}
                    </div>
                </span>
                <div className="nav-user-container">
                    <div className="user-menu-container">
                        <span>{formatMessage(messages.greeting)}</span>
                        <Menu className="user-menu" label={firstName} menuItems={menuItems} />
                    </div>
                    {!userRole.orgLevelQuickChanger || user.orgLevelList.length === 0 ? null : (
                        <div className="quick-changer-nolink-container">
                            <div onClick={() => toggleOpenSelectCompany(true)}>
                                <LocationIcon />
                            </div>
                            <NoLink
                                className="quick-changer-nolink"
                                onClick={() => toggleOpenSelectCompany(true)}
                                label={selectedLocationName}
                                title={user.companyName}
                            />
                        </div>
                    )}
                    <Navigation {...navigationProps} />
                </div>
            </div>

            {!isSelectCompanyOpen ? null : (
                <DialogBox
                    footerType={DialogBoxFooterType.NONE}
                    isOpen={isSelectCompanyOpen}
                    onClose={() => toggleOpenSelectCompany(false)}
                    testId="header-select-company-modal"
                    title={formatMessage(messages.orgLevelQuickChanger)}
                >
                    <SelectCompany
                        onAction={(history) => onRelogin(history)}
                        isQuickChanger={true}
                    />
                </DialogBox>
            )}
        </div>
    );
};

export const Header = injectIntl(Header_);
